/**
 * PURPOSE: Reglas puras del modal "Pruebas que suman en la clasificación" de Atletismo (Winscore).
 * RESPONSIBILITIES: Agrupar `pruebasContempladas` (la lista que arma el BACKEND con la misma función que calcula la
 *   clasificación) por rama (Damas / Varones / Mixtas) y nivel (Noveles / Avanzados), y dar el texto del puntaje por
 *   puesto desde `puntajePorPuesto` (POINTS_TABLE x multiplicador del servidor). No inventa pruebas ni puntos.
 * COLLABORATORS: components/PruebasQueSumanModal.jsx, components/StandingsView.jsx; pruebas en standingsPruebas.test.js.
 * ROUTE: winscore/src/area/AtletismoResultadosArea/standingsPruebas.js
 */
const BRANCHES = Object.freeze([
  { key: "F", label: "Damas" },
  { key: "M", label: "Varones" },
  { key: "X", label: "Mixtas" },
]);
const LEVELS = Object.freeze([
  { key: "NV", label: "Noveles" },
  { key: "AZ", label: "Avanzados" },
]);

export const KIND_LABELS = Object.freeze({ individual: "Pruebas individuales", posta: "Postas", combinada: "Combinadas" });
export const KIND_TAGS = Object.freeze({ posta: "Posta", combinada: "Combinada" });

/** [{ key, label, levels: [{ key, label, pruebas }] }] sin ramas ni niveles vacíos; las pruebas conservan el orden del servidor. */
export const groupPruebasContempladas = (list) => {
  const pruebas = Array.isArray(list) ? list : [];
  const known = new Set(BRANCHES.map((branch) => branch.key));
  const groups = BRANCHES.map((branch) => ({
    ...branch,
    levels: LEVELS.map((level) => ({
      ...level,
      pruebas: pruebas.filter((item) => item.branch?.code === branch.key && item.level?.code === level.key),
    })).filter((level) => level.pruebas.length),
  }));
  // Un nivel o una rama que el servidor mande y aquí no se conozca no se pierde: va al final con su nombre.
  const strays = pruebas.filter((item) => !known.has(item.branch?.code) || !LEVELS.some((level) => level.key === item.level?.code));
  if (strays.length) groups.push({ key: "otras", label: "Otras", levels: [{ key: "otras", label: "", pruebas: strays }] });
  return groups.filter((group) => group.levels.length);
};

const ordinal = (position) => `${position}.º`;

/** "1.º 20 · 2.º 16 · …" de un `puntajePorPuesto[i].points`. */
export const pointsLine = (points) => (Array.isArray(points) ? points.map((value, index) => `${ordinal(index + 1)} ${String(value).replace(".", ",")}`).join(" · ") : "");

/** [{ title, line }]: una línea por multiplicador, con los tipos de prueba a los que aplica ("Pruebas individuales, Postas"). */
export const scoringLines = (puntajePorPuesto) =>
  (Array.isArray(puntajePorPuesto) ? puntajePorPuesto : []).map((entry) => ({
    title: (entry.appliesTo || []).map((kind) => KIND_LABELS[kind] || kind).join(", "),
    multiplier: entry.multiplier,
    line: pointsLine(entry.points),
  }));
